// src/lib/stats-admin-inputs.js — Vue « Statistiques » admin (Gestion).
//
// ⚠️ MODULE PUR — ZÉRO I/O. Aucun appel Supabase / réseau / React ici.
//
// Construit les INPUTS de aggregateStats (src/lib/stats-aggregate.js) à partir
// des sources DÉJÀ en mémoire dans App.jsx (Option A, client-only) :
//   - cfg          : config logements (props[].logs[].chambres[])
//   - dataProjete  : blob fbz8-data PROJETÉ + ÉDITÉ (4 calques facturation V2,
//                    taxe directe #690, prorata 5a) — { [mk]: { [lid]: { reservations } } }
//   - blocksByMonth: Map<mk, Map<lid, block[]>> (hook use-pricing-blocks-history,
//                    DÉJÀ enrichie b.taxe_sejour). Direct interne / sous-loc.
//   - period       : Array<mk "YYYY-MM"> à agréger.
//   - scopeLids    : Array<lid> — le périmètre choisi (admin voit TOUT, aucun
//                    cloisonnement anti-IDOR ici, contrairement à handleProprioStats).
//
// C'est la transposition CLIENT du gabarit serveur handleProprioStats
// (lib/proprio-handlers.js l.1325-1361) SANS la contrainte `scope` JWT : ici
// `scopeLids` est fourni par le sélecteur de scope de l'UI (tous / hors sous-loc /
// par-logement). On itère TOUJOURS scopeLids (jamais Object.keys(dataProjete[mk]),
// même règle que le serveur — itérer une liste explicite, pas le blob global).
//
// Retour : { reservationsByLidMk, directBlocksByLidMk, logsByLid, allResasByLid,
//            nbLogements } — prêt à étaler dans aggregateStats(...).

import { getAllLogsFlat } from './sousloc-helpers'
import { isSousLocLid } from './calc'

// Taux de commission effectif d'un log. Number ou 0.
//
// ⚠️ LOT STATISTIQUES (mesure 1, 2026-09) : une chambre SOUS-LOC n'a JAMAIS de
// taux de commission — le modèle sous-location est marge = CA − loyer − charges
// (buildSousLocStats), pas une commission. L'ancien fallback « taux du parent »
// faisait appliquer le modèle commission aux chambres lc1-3 → marge fausse.
// Ici : sous-loc → 0, toujours. Le fallback parent reste pour les autres cas.
function effectiveTaux(log) {
  if (isSousLocLid(log?.id)) return 0
  if (Number.isFinite(Number(log?.taux))) return Number(log.taux)
  if (Number.isFinite(Number(log?.parentLog?.taux))) return Number(log.parentLog.taux)
  return 0
}

// Mois "YYYY-MM" d'une date ISO, ou null.
function mkOfDate(iso) {
  return typeof iso === 'string' && /^\d{4}-\d{2}/.test(iso) ? iso.slice(0, 7) : null
}

// ── buildAdminStatsInputs(...) ──
export function buildAdminStatsInputs({
  cfg,
  dataProjete,
  blocksByMonth,
  period,
  scopeLids,
} = {}) {
  const months = Array.isArray(period) ? period : []
  const lids = Array.isArray(scopeLids) ? scopeLids : []
  const lidSet = new Set(lids)
  const proj = dataProjete || {}

  // ── 1) reservationsByLidMk : { [lid]: { [mk]: Array<résa projetée> } } ──
  // Transposé de handleProprioStats l.1325-1332 — itère scope × period.
  const reservationsByLidMk = {}
  for (const lid of lids) {
    reservationsByLidMk[lid] = {}
    for (const mk of months) {
      reservationsByLidMk[lid][mk] =
        (proj[mk] && proj[mk][lid] && proj[mk][lid].reservations) || []
    }
  }

  // ── 2) allResasByLid (Option B) : TOUTES les résas du lid, TOUS mois ──
  // Une résa à cheval ne vit que dans UN bucket (son mois majoritaire) ; pour
  // compter ses nuits dans CHAQUE mois de la période, le moteur l'aplatit. On
  // ITÈRE scope (jamais Object.keys du blob global) et on SKIP les namespaces
  // racine `_*` (tombstones / overrides — pas des buckets de mois). GOTCHA
  // namespace : sans ce skip, `_tombstones`/`_monthOverrides`/`_…` seraient
  // traités comme des mois → résas fantômes.
  const allResasByLid = {}
  for (const lid of lids) allResasByLid[lid] = []
  for (const mk of Object.keys(proj)) {
    if (!/^\d{4}-\d{2}$/.test(mk)) continue // skip `_*` et clés non-mois
    const monthBlob = proj[mk]
    if (!monthBlob) continue
    for (const lid of lids) {
      const resas = monthBlob[lid] && monthBlob[lid].reservations
      if (Array.isArray(resas) && resas.length) {
        allResasByLid[lid].push(...resas)
      }
    }
  }

  // ── 3) directBlocksByLidMk : Map<mk, Map<lid, block[]>> RESTREINTE au scope ──
  // PIÈGE : aggregateStats réunit les lids des résas ET des blocs (directLids).
  // Si on passait blocksByMonth tel quel, les blocs lc1/lc2/lc3 repasseraient par
  // directLids même en mode « Tous (hors sous-loc) » → la sous-loc fuirait dans
  // l'agrégat. On reconstruit donc une Map ne contenant QUE les lids du scope.
  const directBlocksByLidMk = new Map()
  if (blocksByMonth && typeof blocksByMonth.forEach === 'function') {
    blocksByMonth.forEach((monthMap, mk) => {
      if (!monthMap || typeof monthMap.forEach !== 'function') return
      let restricted = null
      monthMap.forEach((blocks, lid) => {
        if (!lidSet.has(lid)) return
        if (!restricted) restricted = new Map()
        restricted.set(lid, blocks)
      })
      if (restricted) directBlocksByLidMk.set(mk, restricted)
    })
  }

  // ── 4) logsByLid : { [lid]: { ...log, taux: effectiveTaux } } ──
  // getAllLogsFlat dédup byId + attache parentLog (héritage taux sous-loc). On
  // filtre au scope et on résout le taux comme la projection (effectiveTaux).
  const flat = getAllLogsFlat(cfg)
  const byId = {}
  for (const l of flat) byId[l.id] = l
  const logsByLid = {}
  for (const lid of lids) {
    const log = byId[lid] || null
    logsByLid[lid] = { ...(log || {}), taux: effectiveTaux(log) }
  }

  // ── 5) nbLogementsByMk (mesure 3, lot Statistiques 2026-09) ──
  // L'ancien dénominateur d'occupation était CONSTANT (nbLogements ×  jours)
  // sur toute la fenêtre : les mois d'AVANT l'entrée en gestion d'un logement
  // comptaient des nuits disponibles fictives et diluaient le taux (46 % → 31 %
  // mesuré). Ici : un logement n'entre au dénominateur d'un mois que s'il était
  // déjà EN SERVICE ce mois-là — « en service » = à partir de son premier mois
  // d'activité observée (première résa, tous buckets confondus, ou premier bloc
  // direct interne). Un logement sans aucune activité ne compte jamais.
  // aggregateStats consomme ce champ additif (fallback nbLogements si absent).
  const firstActivityMk = {}
  const noteActivity = (lid, mk) => {
    if (!mk) return
    if (!firstActivityMk[lid] || mk < firstActivityMk[lid]) firstActivityMk[lid] = mk
  }
  for (const lid of lids) {
    for (const r of allResasByLid[lid]) {
      noteActivity(lid, mkOfDate(r?.checkIn || r?.start_date || r?.date))
    }
  }
  // Fallback : résa sans date lisible → son bucket de mois vaut activité.
  for (const mk of Object.keys(proj)) {
    if (!/^\d{4}-\d{2}$/.test(mk)) continue
    const monthBlob = proj[mk]
    if (!monthBlob) continue
    for (const lid of lids) {
      const resas = monthBlob[lid] && monthBlob[lid].reservations
      if (Array.isArray(resas) && resas.length) noteActivity(lid, mk)
    }
  }
  if (blocksByMonth && typeof blocksByMonth.forEach === 'function') {
    blocksByMonth.forEach((monthMap, mk) => {
      if (!/^\d{4}-\d{2}$/.test(mk)) return
      if (!monthMap || typeof monthMap.forEach !== 'function') return
      monthMap.forEach((blocks, lid) => {
        if (lidSet.has(lid) && Array.isArray(blocks) && blocks.length) {
          noteActivity(lid, mk)
        }
      })
    })
  }
  const nbLogementsByMk = {}
  for (const mk of months) {
    let n = 0
    for (const lid of lids) {
      if (firstActivityMk[lid] && firstActivityMk[lid] <= mk) n += 1
    }
    nbLogementsByMk[mk] = n
  }

  return {
    reservationsByLidMk,
    directBlocksByLidMk,
    logsByLid,
    allResasByLid,
    nbLogements: lids.length,
    nbLogementsByMk,
  }
}
